import React, { useState, useRef, useEffect } from 'react';
import { Send, Plus, Paperclip, X, ShoppingCart, FileText, Package, UsersRound, CalendarCog } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../features/language/useLanguage';
import { assistantService } from '../../../services/assistantService';
import type { MessageAttachment } from '../../../types/assistant';

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string;          // object URL for the local thumbnail
  uploading: boolean;
  error?: string;
  uploaded?: MessageAttachment; // set once the upload resolves
}

interface ChatInputProps {
  onSend: (message: string, attachments: MessageAttachment[]) => void;
  disabled?: boolean;
  showQuickActions?: boolean;
  imageEnabled?: boolean; // false → hide the attach button (model can't see images / no GCS)
}

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const MAX_TEXTAREA_HEIGHT = 200;

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  showQuickActions = false,
  imageEnabled = false,
}) => {
  const { t } = useLanguage();
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploading = attachments.some(a => a.uploading);
  const readyAttachments = attachments.filter(a => a.uploaded).map(a => a.uploaded!) as MessageAttachment[];

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files) return;
    const picked = Array.from(files);
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-picking the same file

    for (const file of picked) {
      const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setAttachments(prev => [...prev, { id, file, previewUrl: '', uploading: false, error: t('aiAssistant.image.badType') }]);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setAttachments(prev => [...prev, { id, file, previewUrl: '', uploading: false, error: t('aiAssistant.image.tooLarge') }]);
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      setAttachments(prev => [...prev, { id, file, previewUrl, uploading: true }]);
      try {
        const uploaded = await assistantService.uploadAssistantImage(file);
        setAttachments(prev => prev.map(a => (a.id === id ? { ...a, uploading: false, uploaded } : a)));
      } catch {
        setAttachments(prev => prev.map(a => (a.id === id ? { ...a, uploading: false, error: t('aiAssistant.image.uploadFailed') } : a)));
      }
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const target = prev.find(a => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  };

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSend = () => {
    const trimmed = value.trim();
    if ((!trimmed && readyAttachments.length === 0) || disabled || isUploading) return;
    onSend(trimmed, readyAttachments);
    setValue('');
    attachments.forEach(a => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (text: string) => {
    setValue(text);
    setShowTools(false);
    textareaRef.current?.focus();
  };

  const quickActions = [
    { icon: ShoppingCart, label: t('aiAssistant.chips.createPurchaseOrder'), prompt: t('aiAssistant.prompts.createPurchaseOrder'), color: 'text-indigo-400' },
    { icon: FileText, label: t('aiAssistant.chips.createSalesOrder'), prompt: t('aiAssistant.prompts.createSalesOrder'), color: 'text-emerald-400' },
    { icon: Package, label: t('aiAssistant.chips.createInventory'), prompt: t('aiAssistant.prompts.createInventory'), color: 'text-amber-400' },
    { icon: UsersRound, label: t('aiAssistant.chips.createSupplier'), prompt: t('aiAssistant.prompts.createSupplier'), color: 'text-purple-400' },
    { icon: UsersRound, label: t('aiAssistant.chips.createCustomer'), prompt: t('aiAssistant.prompts.createCustomer'), color: 'text-emerald-400' },
    { icon: CalendarCog, label: t('aiAssistant.chips.createWorkOrderDraft'), prompt: t('aiAssistant.prompts.createWorkOrderDraft'), color: 'text-yellow-400' },
  ];

  return (
    <div className="flex-none w-full space-y-3 relative" data-test-id="chat-input-container">
      {/* Quick Actions Popup */}
      {showTools && (
        <div
          ref={popupRef}
          className="absolute bottom-full left-0 mb-3 w-72 bg-[#1c1c26] border border-white/10 rounded-2xl p-2 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 z-50"
        >
          <div className="text-xs font-semibold text-gray-500 px-3 py-2 uppercase tracking-wider">
            {t('aiAssistant.quickActions.title')}
          </div>
          <div className="space-y-1">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickAction(action.prompt)}
                className="w-full flex items-center space-x-3 px-3 py-2.5 hover:bg-white/5 rounded-xl transition-colors text-sm text-gray-300 text-left"
                data-test-id={`quick-action-${idx}`}
              >
                <action.icon className={cn('h-4 w-4', action.color)} />
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input for image attachments */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="hidden"
        onChange={e => handleFilesSelected(e.target.files)}
        data-test-id="chat-image-input"
      />

      {/* Attachment preview chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-test-id="chat-attachment-previews">
          {attachments.map(att => (
            <div key={att.id} className="relative">
              {att.previewUrl ? (
                <img src={att.previewUrl} alt={att.file.name} className="h-16 w-16 rounded-lg object-cover border border-white/10" />
              ) : (
                <div className="h-16 w-16 rounded-lg border border-red-500/40 bg-red-500/10 flex items-center justify-center p-1 text-[10px] text-red-300 text-center">
                  {att.error}
                </div>
              )}
              {att.uploading && (
                <div className="absolute inset-0 rounded-lg bg-black/50 flex items-center justify-center">
                  <div className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAttachment(att.id)}
                className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-gray-900 border border-white/20 text-gray-300 hover:text-white"
                data-test-id="chat-attachment-remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Box */}
      <div
        className={cn(
          'bg-[#16161e]/80 backdrop-blur-md border transition-all duration-300 rounded-3xl p-3 flex flex-col',
          isFocused && !disabled
            ? 'border-indigo-500/50 ring-4 ring-indigo-500/5 shadow-2xl'
            : 'border-white/10',
          disabled && 'opacity-60 cursor-not-allowed'
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={disabled}
          placeholder={t('aiAssistant.messages.inputPlaceholder')}
          className={cn(
            'w-full bg-transparent border-none focus:ring-0 focus:outline-none resize-none text-base md:text-lg placeholder:text-gray-500 px-3 pt-2 text-gray-200',
            disabled && 'cursor-not-allowed'
          )}
          data-test-id="chat-input-textarea"
        />

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
          <div className="flex items-center gap-1">
            {showQuickActions && (
              <button
                onClick={() => setShowTools(!showTools)}
                className={cn(
                  'p-2 rounded-full transition-all duration-300',
                  showTools
                    ? 'bg-indigo-600 text-white rotate-45 shadow-lg shadow-indigo-600/40'
                    : 'hover:bg-gray-800 text-gray-400 hover:text-white'
                )}
                data-test-id="chat-quick-actions-toggle"
              >
                <Plus className="h-5 w-5" />
              </button>
            )}
            {imageEnabled && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="p-2 rounded-full hover:bg-gray-800 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                title={t('aiAssistant.image.attach')}
                data-test-id="chat-attach-button"
              >
                <Paperclip className="h-5 w-5" />
              </button>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={(!value.trim() && readyAttachments.length === 0) || disabled || isUploading}
            className={cn(
              'flex items-center space-x-2 px-5 py-2 rounded-xl transition-all font-medium',
              (value.trim() || readyAttachments.length > 0) && !disabled && !isUploading
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20'
                : 'bg-gray-800 text-gray-500 cursor-not-allowed'
            )}
            data-test-id="chat-send-button"
          >
            <span className="text-sm">{t('aiAssistant.messages.send')}</span>
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
